import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Col, Form, InputGroup, Pagination, Row, Spinner, Table } from 'react-bootstrap';
import { FiAlertCircle, FiDownload, FiEye, FiEdit, FiGift, FiMail, FiPlus, FiRefreshCw, FiSearch, FiTrash2, FiUpload } from 'react-icons/fi';
import {
    deleteCommercialGesture,
    listCommercialGestures,
    sendCommercialGestureRibLink,
} from '../../services/api';
import {
    CG_DEFAULT_PER_PAGE,
    CG_PER_PAGE_OPTIONS,
    CG_STATUSES,
    CG_STATUS_CONFIG,
    canSendCgRibLink,
    formatCgAmount,
    formatCgDate,
    getCgErrorToast,
    parseCgError,
    unwrapCgList,
} from './commercialGestures/commercialGestureUtils';
import {
    CgDeleteConfirmModal,
    CgSendRibLinkModal,
    CgToastStack,
    CommercialGestureStatusBadge,
    useCgToasts,
    useCommercialGesturePermissions,
} from './commercialGestures/CommercialGestureShared';
import './commercialGestures/commercialGestures.css';

const DEFAULTS = {
    page: '1',
    per_page: String(CG_DEFAULT_PER_PAGE),
    status: '',
    policy_code: '',
    reservation_number: '',
    search: '',
};

const readFilters = (searchParams) => {
    const perPageRaw = Number(searchParams.get('per_page')) || CG_DEFAULT_PER_PAGE;
    const perPage = CG_PER_PAGE_OPTIONS.includes(perPageRaw) ? perPageRaw : CG_DEFAULT_PER_PAGE;
    return {
        page: Math.max(1, Number(searchParams.get('page')) || 1),
        per_page: perPage,
        status: searchParams.get('status') || '',
        policy_code: searchParams.get('policy_code') || '',
        reservation_number: searchParams.get('reservation_number') || '',
        search: searchParams.get('search') || '',
    };
};

const AdminCommercialGestures = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { canRead, canWrite } = useCommercialGesturePermissions();
    const { toasts, push, dismiss } = useCgToasts();
    const filters = useMemo(() => readFilters(searchParams), [searchParams]);

    const [searchDraft, setSearchDraft] = useState(filters.search);
    const [policyDraft, setPolicyDraft] = useState(filters.policy_code);
    const [reservationDraft, setReservationDraft] = useState(filters.reservation_number);
    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0, per_page: CG_DEFAULT_PER_PAGE });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toDelete, setToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [toSend, setToSend] = useState(null);
    const [sending, setSending] = useState(false);
    const sendInFlightRef = useRef(false);

    useEffect(() => { setSearchDraft(filters.search); }, [filters.search]);
    useEffect(() => { setPolicyDraft(filters.policy_code); }, [filters.policy_code]);
    useEffect(() => { setReservationDraft(filters.reservation_number); }, [filters.reservation_number]);

    const replaceParams = useCallback((patch, resetPage = true) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            Object.entries(patch).forEach(([key, value]) => {
                if (value === null || value === undefined || String(value) === '' || String(value) === DEFAULTS[key]) {
                    next.delete(key);
                } else {
                    next.set(key, String(value));
                }
            });
            if (resetPage) next.delete('page');
            return next;
        });
    }, [setSearchParams]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchDraft !== filters.search) replaceParams({ search: searchDraft });
        }, 300);
        return () => clearTimeout(timer);
    }, [searchDraft, filters.search, replaceParams]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (policyDraft !== filters.policy_code) replaceParams({ policy_code: policyDraft });
        }, 300);
        return () => clearTimeout(timer);
    }, [policyDraft, filters.policy_code, replaceParams]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (reservationDraft !== filters.reservation_number) {
                replaceParams({ reservation_number: reservationDraft });
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [reservationDraft, filters.reservation_number, replaceParams]);

    const load = useCallback(async () => {
        if (!canRead) return;
        setLoading(true);
        setError(null);
        try {
            const response = await listCommercialGestures({
                page: filters.page,
                per_page: filters.per_page,
                status: filters.status,
                policy_code: filters.policy_code,
                reservation_number: filters.reservation_number,
                search: filters.search,
            });
            const { items: rows, meta: pageMeta } = unwrapCgList(response);
            setItems(rows);
            setMeta({
                current_page: pageMeta.current_page || filters.page,
                per_page: pageMeta.per_page || filters.per_page,
                total: pageMeta.total || 0,
                last_page: pageMeta.last_page || 1,
            });
        } catch (err) {
            const parsed = parseCgError(err);
            setError(getCgErrorToast(parsed));
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [canRead, filters]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async () => {
        if (!toDelete?.id) return;
        setDeleting(true);
        try {
            await deleteCommercialGesture(toDelete.id);
            setToDelete(null);
            push('Le geste commercial a été supprimé.');
            load();
        } catch (err) {
            push(getCgErrorToast(parseCgError(err)), 'danger');
        } finally {
            setDeleting(false);
        }
    };

    const handleSendRibLink = async () => {
        if (sendInFlightRef.current || !toSend?.id) return;
        sendInFlightRef.current = true;
        setSending(true);
        try {
            await sendCommercialGestureRibLink(toSend.id);
            const resent = toSend.status === 'send';
            setToSend(null);
            push(resent ? 'Le lien RIB a été renvoyé.' : 'Le lien RIB a été envoyé.');
            load();
        } catch (err) {
            push(getCgErrorToast(parseCgError(err)), 'danger');
        } finally {
            sendInFlightRef.current = false;
            setSending(false);
        }
    };

    const lastPage = Math.max(1, meta.last_page || 1);
    const currentPage = Math.min(filters.page, lastPage);
    const from = meta.total === 0 ? 0 : ((currentPage - 1) * filters.per_page) + 1;
    const to = Math.min(currentPage * filters.per_page, meta.total);
    const hasActiveFilters = Boolean(
        filters.search || filters.status || filters.policy_code || filters.reservation_number
    );

    const handleReset = useCallback(() => {
        setSearchDraft('');
        setPolicyDraft('');
        setReservationDraft('');
        setSearchParams({});
    }, [setSearchParams]);

    if (!canRead) {
        return (
            <div className="dashboard-page admin-commercial-gestures-page">
                <Alert variant="danger">Action non autorisée</Alert>
            </div>
        );
    }

    return (
        <div className="dashboard-page admin-commercial-gestures-page">
            <CgToastStack toasts={toasts} onDismiss={dismiss} />
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <span className="page-title-icon" aria-hidden="true">
                                    <FiGift />
                                </span>
                                Gestes commerciaux
                            </h2>
                            <p className="text-muted mb-0">
                                Gestes commerciaux des polices Standard (un geste par police).
                            </p>
                        </div>
                        <div className="d-flex gap-2 page-header-actions">
                            <Button
                                variant="outline-primary"
                                className="btn-refresh"
                                onClick={load}
                                disabled={loading}
                            >
                                <FiRefreshCw className={loading ? 'spinning' : ''} /> Actualiser
                            </Button>
                            {canWrite && (
                                <>
                                    <Button
                                        variant="outline-success"
                                        onClick={() => navigate('/admin/trips/commercial-gestures/bulk-transfers')}
                                    >
                                        <FiDownload className="me-1" /> Fichiers de virement
                                    </Button>
                                    <Button
                                        variant="outline-warning"
                                        onClick={() => navigate('/admin/trips/commercial-gestures/bulk-payments')}
                                    >
                                        <FiUpload className="me-1" /> Import paiements
                                    </Button>
                                    <Button
                                        variant="primary"
                                        className="btn-create"
                                        onClick={() => navigate('/admin/trips/commercial-gestures/new')}
                                    >
                                        <FiPlus /> Nouveau geste commercial
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>

                    {error && (
                        <Alert variant="danger" className="mt-3" role="alert">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <FiAlertCircle aria-hidden="true" />
                                <span className="flex-grow-1">{error}</span>
                                <Button variant="outline-danger" size="sm" onClick={load}>
                                    <FiRefreshCw className="me-1" aria-hidden="true" />
                                    Réessayer
                                </Button>
                            </div>
                        </Alert>
                    )}

                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Filtres de recherche</h6>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleReset}
                                disabled={!hasActiveFilters}
                            >
                                Réinitialiser
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3" controlId="cg-filter-search">
                                        <Form.Label>Recherche</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text aria-hidden="true">
                                                <FiSearch />
                                            </InputGroup.Text>
                                            <Form.Control
                                                value={searchDraft}
                                                onChange={(e) => setSearchDraft(e.target.value)}
                                                placeholder="Prénom, nom, mail, police, réservation…"
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3" controlId="cg-filter-status">
                                        <Form.Label>Statut</Form.Label>
                                        <Form.Select
                                            value={filters.status}
                                            onChange={(e) => replaceParams({ status: e.target.value })}
                                        >
                                            <option value="">Tous</option>
                                            {CG_STATUSES.map((status) => (
                                                <option key={status} value={status}>{CG_STATUS_CONFIG[status].label}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3" controlId="cg-filter-policy">
                                        <Form.Label>Code police</Form.Label>
                                        <Form.Control
                                            value={policyDraft}
                                            onChange={(e) => setPolicyDraft(e.target.value)}
                                            placeholder="Exact"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3" controlId="cg-filter-reservation">
                                        <Form.Label>N° de réservation</Form.Label>
                                        <Form.Control
                                            value={reservationDraft}
                                            onChange={(e) => setReservationDraft(e.target.value)}
                                            placeholder="Exact"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Chargement des gestes commerciaux...</p>
                        </div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-5">
                            <FiGift size={48} className="text-muted mb-3" />
                            <p className="text-muted mb-0">
                                {error
                                    ? 'Impossible de charger les gestes commerciaux'
                                    : hasActiveFilters
                                        ? 'Aucun geste commercial trouvé avec ces filtres'
                                        : 'Aucun geste commercial disponible'}
                            </p>
                            {hasActiveFilters && !error && (
                                <Button variant="outline-secondary" size="sm" className="mt-3" onClick={handleReset}>
                                    Réinitialiser les filtres
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className="table-responsive table-scroll">
                            <Table hover className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Code police</th>
                                        <th>Réservation</th>
                                        <th>Prénom</th>
                                        <th>Nom</th>
                                        <th>Mail</th>
                                        <th>Montant</th>
                                        <th>Statut</th>
                                        <th>Créé le</th>
                                        <th>Modifié le</th>
                                        <th className="text-end col-actions">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((row) => (
                                        <tr key={row.id}>
                                            <td className="font-monospace">{row.policy_code || '—'}</td>
                                            <td>{row.reservation_number || '—'}</td>
                                            <td>{row.first_name || '—'}</td>
                                            <td>{row.last_name || '—'}</td>
                                            <td>{row.mail || '—'}</td>
                                            <td>{formatCgAmount(row.amount)}</td>
                                            <td><CommercialGestureStatusBadge status={row.status} /></td>
                                            <td>{formatCgDate(row.created_at)}</td>
                                            <td>{formatCgDate(row.updated_at)}</td>
                                            <td className="text-end col-actions">
                                                <Button
                                                    variant="outline-info"
                                                    size="sm"
                                                    className="me-2"
                                                    title="Détails"
                                                    aria-label="Détails"
                                                    onClick={() => navigate(`/admin/trips/commercial-gestures/${row.id}`)}
                                                >
                                                    <FiEye />
                                                </Button>
                                                {canWrite && (
                                                    <>
                                                        <Button
                                                            variant="outline-success"
                                                            size="sm"
                                                            className="me-2"
                                                            title={
                                                                row.status === 'send'
                                                                    ? 'Le lien RIB a déjà été envoyé pour ce geste commercial.'
                                                                    : 'Envoyer le lien RIB'
                                                            }
                                                            aria-label={
                                                                row.status === 'send'
                                                                    ? 'Le lien RIB a déjà été envoyé pour ce geste commercial.'
                                                                    : 'Envoyer le lien RIB'
                                                            }
                                                            disabled={!canSendCgRibLink(row.status) || sending}
                                                            onClick={() => setToSend(row)}
                                                        >
                                                            {sending && toSend?.id === row.id ? (
                                                                <Spinner animation="border" size="sm" />
                                                            ) : (
                                                                <FiMail />
                                                            )}
                                                        </Button>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            className="me-2"
                                                            title="Modifier"
                                                            aria-label="Modifier"
                                                            onClick={() => navigate(`/admin/trips/commercial-gestures/${row.id}/edit`)}
                                                        >
                                                            <FiEdit />
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            title="Supprimer"
                                                            aria-label="Supprimer"
                                                            onClick={() => setToDelete(row)}
                                                        >
                                                            <FiTrash2 />
                                                        </Button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {meta.total > 0 && (
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-4">
                            <div className="text-muted">
                                Affichage de {from} à {to} sur {meta.total} geste{meta.total > 1 ? 's' : ''} commercial{meta.total > 1 ? 's' : ''}
                            </div>
                            <div className="d-flex align-items-center gap-3">
                                <Form.Select
                                    size="sm"
                                    style={{ width: 'auto' }}
                                    value={filters.per_page}
                                    onChange={(e) => replaceParams({ per_page: Number(e.target.value) })}
                                    aria-label="Lignes par page"
                                >
                                    {CG_PER_PAGE_OPTIONS.map((size) => (
                                        <option key={size} value={size}>{size} / page</option>
                                    ))}
                                </Form.Select>
                                {lastPage > 1 && (
                                    <Pagination className="mb-0">
                                        <Pagination.First
                                            disabled={currentPage <= 1}
                                            onClick={() => replaceParams({ page: 1 }, false)}
                                        />
                                        <Pagination.Prev
                                            disabled={currentPage <= 1}
                                            onClick={() => replaceParams({ page: currentPage - 1 }, false)}
                                        />
                                        <Pagination.Item active>{currentPage}</Pagination.Item>
                                        <Pagination.Next
                                            disabled={currentPage >= lastPage}
                                            onClick={() => replaceParams({ page: currentPage + 1 }, false)}
                                        />
                                        <Pagination.Last
                                            disabled={currentPage >= lastPage}
                                            onClick={() => replaceParams({ page: lastPage }, false)}
                                        />
                                    </Pagination>
                                )}
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <CgSendRibLinkModal
                show={Boolean(toSend)}
                gesture={toSend}
                sending={sending}
                onCancel={() => !sending && setToSend(null)}
                onConfirm={handleSendRibLink}
            />
            <CgDeleteConfirmModal
                show={Boolean(toDelete)}
                gesture={toDelete}
                deleting={deleting}
                onCancel={() => setToDelete(null)}
                onConfirm={handleDelete}
            />
        </div>
    );
};

export default AdminCommercialGestures;
